/**
 * API service for communicating with PHP backend
 */

import { getApiBaseUrl } from '@utils/appBase';
import { devLog } from '@utils/devLog';

const API_BASE_URL = getApiBaseUrl();

if (typeof window !== 'undefined') {
	devLog('[API] API_BASE_URL configured as:', API_BASE_URL, 'MODE:', import.meta.env.MODE);
}

export interface ApiResponse<T> {
	success: boolean;
	data?: T;
	error?: string;
	details?: any;
}

function isWriteBlockedForSession(endpoint: string): boolean {
	if (typeof window === 'undefined') return false;
	try {
		const raw = window.sessionStorage.getItem('gardenhub:auth');
		if (!raw) return false;
		const user = JSON.parse(raw) as { isRestricted?: boolean } | null;
		if (!user?.isRestricted) return false;

		const exempt = ['/email-verification/resend', '/login', '/register', '/google-auth'];
		return !exempt.some((path) => endpoint.startsWith(path));
	} catch {
		return false;
	}
}

/**
 * Make a GET request to the API
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
	// Ensure endpoint doesn't start with /api/ since API_BASE_URL already includes it
	const cleanEndpoint = endpoint.startsWith('/api/') ? endpoint.replace('/api/', '/') : endpoint;
	const fullUrl = `${API_BASE_URL}${cleanEndpoint}`;
	devLog('[API] GET', fullUrl);
	const response = await fetch(fullUrl, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	});

	// Check if response is empty
	const text = await response.text();
	if (!text) {
		throw new Error(`Empty response from server (status: ${response.status})`);
	}

	let data: ApiResponse<T>;
	try {
		data = JSON.parse(text);
	} catch (e) {
		throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
	}

	if (!data.success) {
		const detail = data.details?.details ?? data.details;
		const msg = data.error || 'API request failed';
		throw new Error(typeof detail === 'string' ? `${msg}: ${detail}` : msg);
	}

	if (!response.ok) {
		throw new Error(data.error || `HTTP error! status: ${response.status}`);
	}

	return data.data as T;
}

/**
 * Make a POST request to the API
 */
export async function apiPost<T>(endpoint: string, body: any): Promise<T> {
	if (isWriteBlockedForSession(endpoint)) {
		throw new Error('Your account is currently read-only until you verify your email.');
	}
	// Ensure endpoint doesn't start with /api/ since API_BASE_URL already includes it
	const cleanEndpoint = endpoint.startsWith('/api/') ? endpoint.replace('/api/', '/') : endpoint;
	const fullUrl = `${API_BASE_URL}${cleanEndpoint}`;
	devLog('[API] POST', fullUrl);
	const response = await fetch(fullUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	});

	// Check if response is empty
	const text = await response.text();
	if (!text) {
		throw new Error('Empty response from server');
	}

	let data: ApiResponse<T> | T;
	try {
		data = JSON.parse(text);
	} catch (e) {
		throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
	}

	// Handle both wrapped (ApiResponse) and direct response formats
	if (typeof data === 'object' && data !== null && 'success' in data) {
		const apiResponse = data as ApiResponse<T>;
		if (!apiResponse.success) {
			const detail = apiResponse.details?.details ?? apiResponse.details;
			const msg = apiResponse.error || 'API request failed';
			throw new Error(typeof detail === 'string' ? `${msg}: ${detail}` : msg);
		}
		if (!response.ok) {
			throw new Error(apiResponse.error || `HTTP error! status: ${response.status}`);
		}
		return apiResponse.data as T;
	}

	// Direct response (for endpoints that don't wrap in ApiResponse)
	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}

	return data as T;
}

/**
 * Make a PUT request to the API
 */
export async function apiPut<T>(endpoint: string, body: any): Promise<T> {
	if (isWriteBlockedForSession(endpoint)) {
		throw new Error('Your account is currently read-only until you verify your email.');
	}
	// Ensure endpoint doesn't start with /api/ since API_BASE_URL already includes it
	const cleanEndpoint = endpoint.startsWith('/api/') ? endpoint.replace('/api/', '/') : endpoint;
	const fullUrl = `${API_BASE_URL}${cleanEndpoint}`;
	devLog('[API] PUT', fullUrl);
	const response = await fetch(fullUrl, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	});

	// Check if response is empty
	const text = await response.text();
	if (!text) {
		throw new Error(`Empty response from server (status: ${response.status})`);
	}

	let data: ApiResponse<T>;
	try {
		data = JSON.parse(text);
	} catch (e) {
		throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
	}

	if (!data.success) {
		const detail = data.details?.details ?? data.details;
		const msg = data.error || 'API request failed';
		throw new Error(typeof detail === 'string' ? `${msg}: ${detail}` : msg);
	}

	if (!response.ok) {
		throw new Error(data.error || `HTTP error! status: ${response.status}`);
	}

	return data.data as T;
}

/**
 * Make a DELETE request to the API.
 * Pass `body` when the endpoint expects a JSON body (e.g. leave company with `companyId`).
 */
export async function apiDelete<T>(endpoint: string, body?: Record<string, unknown>): Promise<T> {
	if (isWriteBlockedForSession(endpoint)) {
		throw new Error('Your account is currently read-only until you verify your email.');
	}
	// Ensure endpoint doesn't start with /api/ since API_BASE_URL already includes it
	const cleanEndpoint = endpoint.startsWith('/api/') ? endpoint.replace('/api/', '/') : endpoint;
	const fullUrl = `${API_BASE_URL}${cleanEndpoint}`;
	devLog('[API] DELETE', fullUrl);
	const response = await fetch(fullUrl, {
		method: 'DELETE',
		headers: {
			'Content-Type': 'application/json',
		},
		body: body !== undefined ? JSON.stringify(body) : undefined,
	});

	// Check if response is empty
	const text = await response.text();
	if (!text) {
		throw new Error(`Empty response from server (status: ${response.status})`);
	}

	let data: ApiResponse<T>;
	try {
		data = JSON.parse(text);
	} catch (e) {
		throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
	}

	if (!data.success) {
		throw new Error(data.error || 'API request failed');
	}

	if (!response.ok) {
		throw new Error(data.error || `HTTP error! status: ${response.status}`);
	}

	return data.data as T;
}

/**
 * Legacy function - keeping for compatibility but using real API now
 */
export async function fakeRequest<T>(data: T, delayMs = 300): Promise<T> {
	// For backwards compatibility, but we should use real API calls now
	return new Promise((resolve) => setTimeout(() => resolve(structuredClone(data)), delayMs));
}
